import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { GooglePlacesLoaderService } from '@shared/services/google-places-loader.service';

declare var google: any;

@Directive({
    selector: '[ngx-google-places-autocomplete]',
    exportAs: 'ngx-places',
    standalone: false
})
export class GooglePlaceDirective implements AfterViewInit, OnDestroy {
  @Input('options') options: any;
  @Output() onAddressChange: EventEmitter<any> = new EventEmitter();

  private autocomplete: any;
  private loaderSubscription: Subscription;
  public place: any;

  constructor(
    private el: ElementRef,
    private ngZone: NgZone,
    private placesLoader: GooglePlacesLoaderService
  ) {}

  ngAfterViewInit(): void {
    if (!this.options) {
      this.options = {};
    }

    if (this.isGoogleLibExists()) {
      this.initialize();
      return;
    }

    // The Maps script is consent-gated (functional category) and may load
    // late or never; the field stays a plain input until the library arrives.
    this.loaderSubscription = this.placesLoader.loaded$
      .pipe(take(1))
      .subscribe(() => {
        if (this.isGoogleLibExists()) {
          this.initialize();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.loaderSubscription) {
      this.loaderSubscription.unsubscribe();
    }
  }

  private isGoogleLibExists(): boolean {
    return typeof google !== 'undefined' && typeof google.maps !== 'undefined' && typeof google.maps.places !== 'undefined';
  }

  private initialize(): void {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.el.nativeElement,
      this.options
    );

    if (this.autocomplete.addListener != null) {
      this.autocomplete.addListener('place_changed', () => {
        this.handleChangeEvent();
      });
    }

    this.el.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      if (!event.key) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === 'enter' && event.target === this.el.nativeElement) {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }

  public reset(): void {
    this.autocomplete.setComponentRestrictions(this.options.componentRestrictions);
    this.autocomplete.setTypes(this.options.types);
  }

  private handleChangeEvent(): void {
    this.ngZone.run(() => {
      this.place = this.autocomplete.getPlace();
      if (this.place) {
        this.onAddressChange.emit(this.place);
      }
    });
  }
}
